import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, getFns } from '../firebase'
import { readCurrentStoreLocation, storeLocationErrorMessage } from '../lib/storeLocation'
import Button from './ui/Button'
import Card from './ui/Card'
import TimesheetCaptureDialog from './TimesheetCaptureDialog'

const NETWORK_LABELS = {
  allowed: 'Store network matched.', outside_store: 'This connection does not match the store network.',
  not_configured: 'The store network has not been configured.', unavailable: 'The network could not be verified.',
}
const LOCATION_LABELS = {
  inside: 'The location reading is within the store area.', outside: 'The location reading is outside the store area.',
  uncertain: 'The location reading is not accurate enough to confirm the store area.',
  not_configured: 'The store location has not been configured.', unavailable: 'Location was unavailable.', stale: 'The location reading expired.',
}

export function timesheetTime(value) {
  const date = new Date(value)
  return value && !Number.isNaN(date.getTime()) ? date.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : 'the recorded server time'
}

export function TimesheetPresenceReasons({ presence }) {
  return <div className="space-y-1 text-sm text-muted-foreground">
    <p>{NETWORK_LABELS[presence?.network?.status] || NETWORK_LABELS.unavailable}</p>
    <p>{LOCATION_LABELS[presence?.location?.status] || LOCATION_LABELS.unavailable}</p>
  </div>
}

const hasPassed = (presence) => presence?.allowed === true && presence?.network?.status === 'allowed' && presence?.location?.status === 'inside'
const preconditionMessage = (error, fallback) => error?.code === 'functions/failed-precondition' && typeof error.message === 'string'
  ? error.message.replace(/^Firebase:\s*/, '').replace(/\s+\(functions\/failed-precondition\)\.?$/, '') : fallback
const attendanceError = (error, fallback) => error?.code === 'functions/resource-exhausted'
  ? 'Too many attempts. Wait a moment and try again.'
  : ['functions/permission-denied', 'functions/unauthenticated'].includes(error?.code)
    ? 'Your employee account could not be verified. Sign in again or contact Cinara.' : preconditionMessage(error, fallback)

const ACTIONS = {
  startBreak: { callable: 'timesheetStartBreak', label: 'Start break', recorded: 'Unpaid break started', confirmation: 'Start an unpaid break', slide: 'Slide to start break' },
  endBreak: { callable: 'timesheetEndBreak', label: 'Return to work', recorded: 'Returned to work', confirmation: 'Return to work', slide: 'Slide to return to work' },
  clockOut: { callable: 'timesheetClockOut', label: 'Clock out', recorded: 'Clock-out recorded', confirmation: 'End your shift', slide: 'Slide to clock out' },
}

export function timesheetDuration(milliseconds) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000))
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].map(value => String(value).padStart(2, '0')).join(':')
}

function validEntry(entry) {
  return entry === null || (entry && typeof entry.id === 'string' && Number.isInteger(entry.revision)
    && ['working', 'on_break', 'clocked_out'].includes(entry.status)
    && [entry.elapsedMs, entry.workedMs ?? entry.paidWorkMs, entry.breakMs ?? entry.totalBreakMs].every(value => Number.isFinite(value) && value >= 0))
}

export default function TimesheetClockCard({ className = '', onShiftStateChange }) {
  const [signedIn, setSignedIn] = useState(false)
  const [snapshot, setSnapshot] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [statusError, setStatusError] = useState('')
  const [connected, setConnected] = useState(() => navigator.onLine)
  const [phase, setPhase] = useState('')
  const [pending, setPending] = useState(null)
  const [actionAttempt, setActionAttempt] = useState(null)
  const [selectedAction, setSelectedAction] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [tick, setTick] = useState(() => performance.now())
  const epochRef = useRef(0)
  const statusRequestRef = useRef(0)
  const busyRef = useRef(false)
  const loadingRef = useRef(false)
  const refreshAfterActionRef = useRef(false)

  const refreshStatus = useCallback(async ({ force = false } = {}) => {
    if (busyRef.current && !force) { refreshAfterActionRef.current = true; return }
    const user = auth.currentUser
    if (!user) return
    const epoch = epochRef.current, request = ++statusRequestRef.current
    loadingRef.current = true; setLoadingStatus(true); setStatusError('')
    try {
      if (!navigator.onLine) throw new Error('Offline')
      const { data } = await httpsCallable(getFns(), 'timesheetGetStatus')({})
      if (epoch !== epochRef.current || request !== statusRequestRef.current || auth.currentUser?.uid !== user.uid) return
      if (!data?.operator || !Number.isFinite(Date.parse(data.serverNow)) || !validEntry(data.entry) || !validEntry(data.lastEntry)) throw new Error('Invalid timesheet status')
      const receivedAt = performance.now()
      setSnapshot({ ...data, receivedAt }); setTick(receivedAt)
    } catch (err) {
      if (epoch !== epochRef.current || request !== statusRequestRef.current) return
      setSnapshot(null)
      setStatusError(err?.code === 'functions/permission-denied' ? 'This account is not linked to an active employee. Contact Cinara.'
        : err?.code === 'functions/unauthenticated' ? 'Sign in to view your timesheet.'
          : preconditionMessage(err, 'Could not load your timesheet. Check your connection and refresh your status.'))
    } finally {
      if (epoch === epochRef.current && request === statusRequestRef.current) { loadingRef.current = false; setLoadingStatus(false) }
    }
  }, [])

  const clearTransient = useCallback((clearStatus = false) => {
    ++epochRef.current; ++statusRequestRef.current
    busyRef.current = false; loadingRef.current = false; refreshAfterActionRef.current = false
    setPhase(''); setPending(null); setActionAttempt(null); setConfirmed(false); setSelectedAction('')
    setError(''); setNotice(''); setLoadingStatus(false)
    if (clearStatus) { setSnapshot(null); setStatusError('') }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTransient(true); setSignedIn(Boolean(user))
      if (user) refreshStatus()
    })
    const visibility = () => {
      if (document.visibilityState !== 'visible') clearTransient(true)
      else refreshStatus()
    }
    const online = () => { setConnected(true); refreshStatus() }
    const offline = () => setConnected(false)
    document.addEventListener('visibilitychange', visibility)
    window.addEventListener('online', online); window.addEventListener('offline', offline)
    return () => {
      unsubscribe(); ++epochRef.current; ++statusRequestRef.current
      document.removeEventListener('visibilitychange', visibility)
      window.removeEventListener('online', online); window.removeEventListener('offline', offline)
    }
  }, [clearTransient, refreshStatus])

  useEffect(() => {
    if (!snapshot?.entry) return undefined
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') setTick(performance.now()) }, 1000)
    return () => window.clearInterval(timer)
  }, [snapshot?.entry])

  useEffect(() => {
    if (!snapshot?.entry && !actionAttempt) setSelectedAction(current => current === 'clockIn' ? current : '')
  }, [snapshot?.entry?.id, actionAttempt])

  const active = (request) => request.epoch === epochRef.current && auth.currentUser?.uid === request.uid
  const finish = (request) => {
    if (!active(request)) return
    busyRef.current = false; setPhase('')
    if (refreshAfterActionRef.current) { refreshAfterActionRef.current = false; refreshStatus() }
  }
  const start = () => {
    if (busyRef.current || loadingRef.current || !auth.currentUser) return null
    if (!navigator.onLine) { setError('Connect to the internet before recording your time.'); return null }
    busyRef.current = true; setError(''); setNotice('')
    return { epoch: epochRef.current, uid: auth.currentUser.uid }
  }

  const commit = async (session, confirmedAtWork, request, capturedPhoto) => {
    if (!active(request)) return
    const expired = Date.parse(session.expiresAt) <= Date.now()
    if (expired && !session.commitAttempted) {
      setPending(null); setConfirmed(false); setError('This clock-in request expired. Slide again to start a new request.'); return
    }
    let location = session.location
    if (!session.commitPayload && !expired && location && Date.now() - location.timestamp > 45000) {
      setPhase('location')
      try { location = await readCurrentStoreLocation() } catch { location = null }
      if (!active(request)) return
    }
    const photo = session.photo || capturedPhoto
    if (!photo) { setError('Wait for the camera preview, then slide to take your photo.'); return }
    const commitPayload = session.commitPayload || { ticket: session.ticket, location, confirmedAtWork, photo }
    setPending({ ...session, location, photo, commitPayload, commitAttempted: true }); setPhase('commit')
    try {
      const { data } = await httpsCallable(getFns(), 'timesheetCommitClockIn')(commitPayload)
      if (!active(request)) return
      if (data?.status === 'confirmation_required') {
        setPending({ ...session, location, photo: null, commitPayload: null, commitAttempted: true, presence: data.presence, needsConfirmation: true }); setConfirmed(false); return
      }
      if (data?.status !== 'recorded' || !data.entry?.clockInAt) throw new Error('Invalid clock-in response')
      setNotice(`Clock-in recorded at ${timesheetTime(data.entry.clockInAt)}.${data.notificationCreated ? ' An alert for Cinara was saved in the app.' : ''}`)
      setPending(null); setConfirmed(false); setSelectedAction('')
      await refreshStatus({ force: true })
    } catch (err) {
      if (!active(request)) return
      if (err?.code === 'functions/deadline-exceeded') {
        setPending(null); setConfirmed(false); setError('This clock-in request expired. Slide again to start a new request.')
      } else if (err?.code === 'functions/aborted') {
        setPending(null); setConfirmed(false); setError('Your shift changed. Review the refreshed status before continuing.'); await refreshStatus({ force: true })
      } else if (err?.code === 'functions/invalid-argument') {
        setPending({ ...session, photo: null, commitPayload: null }); setError('The photo could not be accepted. Check the camera preview and take a new photo by sliding again.')
      } else setError(preconditionMessage(err, 'Could not confirm that the clock-in was saved. Retry this request to check its result.'))
    }
  }

  const clockIn = async (photo) => {
    if (!photo || pending || actionAttempt || selectedAction !== 'clockIn' || !snapshot || snapshot.entry) return
    const request = start()
    if (!request) return
    setPhase('location')
    let location = null, locationMessage = ''
    try { location = await readCurrentStoreLocation() } catch (err) { locationMessage = storeLocationErrorMessage(err) }
    if (!active(request)) return
    setPhase('prepare')
    try {
      const { data } = await httpsCallable(getFns(), 'timesheetPrepareClockIn')({ location })
      if (!active(request)) return
      if (!data?.ticket || !Number.isFinite(Date.parse(data.expiresAt)) || !data.operator || !data.presence) throw new Error('Invalid clock-in preparation')
      const session = { ...data, location, locationMessage, needsConfirmation: data.confirmationRequired === true || !hasPassed(data.presence) }
      setPending(session); setConfirmed(false)
      if (!session.needsConfirmation) await commit(session, false, request, photo)
    } catch (err) {
      if (!active(request)) return
      setError(attendanceError(err, 'Could not prepare your clock-in. Please try again.'))
      if (['functions/failed-precondition', 'functions/aborted'].includes(err?.code)) await refreshStatus({ force: true })
    } finally { finish(request) }
  }

  const confirmOrRetry = async (photo) => {
    if (!pending || (pending.needsConfirmation && !confirmed)) return
    const request = start()
    if (!request) return
    try { await commit(pending, pending.needsConfirmation && confirmed, request, photo) } finally { finish(request) }
  }

  const shiftAction = async (kind, retry = false, photo = null) => {
    if (pending || (!retry && (!snapshot?.entry || actionAttempt))) return
    if (!retry && selectedAction !== kind) return
    if (!retry && !photo) return
    const request = start()
    if (!request) return
    const attempt = retry ? actionAttempt : { kind, payload: { entryId: snapshot.entry.id, expectedRevision: snapshot.entry.revision, requestId: crypto.randomUUID(), photo } }
    if (!attempt) { finish(request); return }
    setActionAttempt(attempt); setPhase(attempt.kind)
    try {
      const { data } = await httpsCallable(getFns(), ACTIONS[attempt.kind].callable)(attempt.payload)
      if (!active(request)) return
      if (!data?.entry?.id || !data.serverNow) throw new Error('Invalid shift response')
      setActionAttempt(null)
      setSelectedAction('')
      setNotice(`${ACTIONS[attempt.kind].recorded} at ${timesheetTime(attempt.kind === 'clockOut' ? data.entry.clockOutAt : data.serverNow)}.`)
      // An idempotent retry can return an older entry. Always read the current
      // shift before enabling another action or updating its running timers.
      await refreshStatus({ force: true })
    } catch (err) {
      if (!active(request)) return
      if (['functions/aborted', 'functions/failed-precondition', 'functions/permission-denied', 'functions/unauthenticated', 'functions/not-found', 'functions/resource-exhausted', 'functions/invalid-argument'].includes(err?.code)) {
        setActionAttempt(null)
        if (['functions/aborted', 'functions/failed-precondition', 'functions/not-found', 'functions/invalid-argument'].includes(err?.code)) setSelectedAction('')
        setError(attendanceError(err, 'Your shift changed. Review the refreshed status before continuing.'))
        await refreshStatus({ force: true })
      } else setError('Could not confirm that the change was saved. Retry the same request to check its result.')
    } finally { finish(request) }
  }

  const entry = snapshot?.entry
  const displayEntry = entry
  const shiftState = snapshot ? entry?.status || 'off_clock' : 'unknown'
  const offClock = shiftState === 'off_clock'
  useEffect(() => { onShiftStateChange?.(shiftState) }, [onShiftStateChange, shiftState])
  const delta = entry ? Math.max(0, tick - snapshot.receivedAt) : 0
  const workedMs = displayEntry ? (displayEntry.workedMs ?? displayEntry.paidWorkMs) + (entry?.status === 'working' ? delta : 0) : 0
  const breakMs = displayEntry ? (displayEntry.breakMs ?? displayEntry.totalBreakMs) + (entry?.status === 'on_break' ? delta : 0) : 0
  const elapsedMs = displayEntry ? displayEntry.elapsedMs + delta : 0
  const currentBreak = entry?.status === 'on_break' ? entry.breaks?.findLast?.(item => !item.endedAt) || [...(entry.breaks || [])].reverse().find(item => !item.endedAt) : null
  const currentBreakMs = currentBreak ? Math.max(0, Date.parse(snapshot.serverNow) - Date.parse(currentBreak.startedAt)) + delta : 0
  const busy = Boolean(phase) || loadingStatus
  const ready = signedIn && connected && snapshot && !busy
  const statusLabel = entry?.status === 'on_break' ? 'On unpaid break' : entry ? 'Working' : 'Off the clock'
  const cancelPunch = () => { clearTransient(); if (document.visibilityState === 'visible') refreshStatus() }
  const actionKind = actionAttempt?.kind || (selectedAction === 'clockIn' ? '' : selectedAction)
  const openAction = (kind) => { setError(''); setNotice(''); setSelectedAction(kind) }
  const clockInOpen = selectedAction === 'clockIn' || Boolean(pending)
  const dialogOpen = clockInOpen || Boolean(actionKind)
  const phaseMessage = phase === 'location' ? 'Getting a current location reading…' : phase === 'prepare' ? 'Checking your account and workplace connection…' : phase ? 'Saving your time…' : ''

  return <Card padding={offClock ? 'none' : undefined} className={`${className} ${offClock ? 'border-0 bg-transparent shadow-none' : ''}`} aria-label="Timesheet" data-shift-state={shiftState}>
    {!offClock && <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        {entry && <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{snapshot?.operator?.name || 'Your shift'}</p>}
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{snapshot ? statusLabel : loadingStatus ? 'Loading your shift…' : 'Timesheet'}</h2>
        {entry && <p className="mt-2 text-sm text-muted-foreground">{entry.status === 'on_break' ? 'Your work timer is paused during this unpaid break.' : `Clocked in at ${timesheetTime(entry.clockInAt)}.`}</p>}
      </div>
      {entry && <Link to="/timesheet-review" className="inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline">Timesheet alerts</Link>}
    </div>}

    {snapshot && <>
      {displayEntry && <div className="mt-6 grid gap-3 sm:grid-cols-3" aria-label={entry ? 'Current shift totals' : 'Last shift totals'}>
        <div className="rounded-xl border border-border bg-muted/30 p-4"><p className="text-xs text-muted-foreground">{entry ? 'Worked this shift' : 'Worked last shift'}</p><p aria-label="Working time" data-testid="worked-time" className="mt-2 font-mono text-3xl font-semibold tracking-tight text-foreground">{timesheetDuration(workedMs)}</p></div>
        <div className="rounded-xl border border-border p-4"><p className="text-xs text-muted-foreground">Unpaid breaks</p><p aria-label="Unpaid break time" data-testid="break-time" className="mt-2 font-mono text-3xl font-semibold tracking-tight text-foreground">{timesheetDuration(breakMs)}</p></div>
        <div className="rounded-xl border border-border p-4"><p className="text-xs text-muted-foreground">Total shift time</p><p data-testid="elapsed-time" className="mt-2 font-mono text-3xl font-semibold tracking-tight text-foreground">{timesheetDuration(elapsedMs)}</p></div>
      </div>}
      {entry?.status === 'on_break' && <p className="mt-4 rounded-lg bg-warning/10 p-3 text-sm text-foreground">Current unpaid break: <span data-testid="current-break-time" className="font-mono font-semibold">{timesheetDuration(currentBreakMs)}</span></p>}
      {!entry && !pending && !actionAttempt && !selectedAction && <Button type="button" fullWidth variant="success" iconName="LogIn" iconSize={30}
        className="min-h-[112px] rounded-2xl text-2xl font-semibold" disabled={!ready} onClick={() => openAction('clockIn')}>Clock in</Button>}
      {entry && !actionAttempt && !selectedAction && <div className="mt-5 flex flex-wrap gap-2">
        <Button type="button" iconName={entry.status === 'on_break' ? 'Play' : 'Pause'} disabled={!ready || Boolean(pending)} onClick={() => openAction(entry.status === 'on_break' ? 'endBreak' : 'startBreak')}>{entry.status === 'on_break' ? 'Return to work' : 'Start break'}</Button>
        <Button type="button" variant="outline" iconName="LogOut" disabled={!ready || Boolean(pending)} onClick={() => openAction('clockOut')}>Clock out</Button>
      </div>}
    </>}

    {dialogOpen && <TimesheetCaptureDialog key={clockInOpen ? 'clockIn' : actionKind}
      title={clockInOpen ? 'Clock in' : ACTIONS[actionKind].confirmation}
      slideLabel={clockInOpen ? 'Slide to clock in' : ACTIONS[actionKind].slide}
      onClose={cancelPunch} onConfirm={clockInOpen ? (pending ? confirmOrRetry : clockIn) : (photo) => shiftAction(actionKind, Boolean(actionAttempt), photo)}
      disabled={!connected || !signedIn || (clockInOpen && Boolean(pending?.needsConfirmation) && !confirmed)}
      busy={busy} error={error || statusError} status={!connected ? 'Reconnect to the internet before recording your time.' : phaseMessage}
      retainedPhoto={clockInOpen ? pending?.photo : actionAttempt?.payload.photo}
      resetKey={clockInOpen ? `${pending?.ticket}:${confirmed}:${pending?.commitAttempted || false}` : `${actionKind}:${entry?.id}:${entry?.revision}:${actionAttempt?.payload.requestId || ''}`}>
      <p className="text-sm font-medium text-foreground">{pending?.operator.name || snapshot?.operator.name}</p>
      {pending?.needsConfirmation && <>
        <p className="text-sm font-semibold text-warning">We could not confirm that you are at the workplace.</p>
        <TimesheetPresenceReasons presence={pending.presence} />
        {pending.locationMessage && <p className="text-xs leading-relaxed text-muted-foreground">{pending.locationMessage}</p>}
        <label className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={busy} className="mt-1 h-5 w-5 shrink-0 rounded border-border accent-primary" />
          <span>I confirm that I am at the workplace. Cinara will be notified about this clock-in.</span>
        </label>
      </>}
      {pending && !pending.needsConfirmation && !busy && <p className="text-sm text-muted-foreground">Slide again to check the result of this clock-in request.</p>}
      {actionAttempt && <p className="text-sm text-muted-foreground">Slide again to check the result of the same request.</p>}
    </TimesheetCaptureDialog>}
    {error && !dialogOpen && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
    {statusError && !dialogOpen && <p role="alert" className="mt-4 text-sm text-destructive">{statusError}</p>}
    {!connected && <p role="status" className="mt-4 text-sm text-warning">You are offline. Reconnect to confirm your shift status or record a change.</p>}
    {offClock && loadingStatus && !dialogOpen && <p role="status" className="mt-4 text-sm text-muted-foreground">Checking your shift…</p>}
    {notice && !offClock && <p role="status" className="mt-5 rounded-lg border border-success/20 bg-success/10 p-4 text-sm text-foreground">{notice}</p>}
    {(entry || statusError || !connected) && <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
      {entry && <p className="max-w-lg text-xs leading-relaxed text-muted-foreground">Breaks are unpaid and excluded from worked time. Signing out does not end your shift.</p>}
      <Button type="button" variant="ghost" size="sm" iconName="RefreshCw" onClick={() => refreshStatus()} disabled={!signedIn || busy || !connected}>Refresh status</Button>
    </div>}
  </Card>
}
